import React, { useMemo, useCallback } from 'react';
import type { ValidationError } from '@/utils/validation/types';
import { HolidayConfigForm } from '@/components/config_forms/holiday/HolidayConfigForm';
import { cn } from '@/lib/utils';
import { useUserConfigState } from '@/context/UserConfigContext';
import type { ExtendedTier } from '@/types/ExtendedTier';
import type { ClientFormData } from '../new_client/utilities/formDataUtils';

interface ClientHolidayConfigProps {
  formData: ClientFormData;
  handleChange: (field: keyof ClientFormData, value: ClientFormData[keyof ClientFormData]) => void;
  handleSwitchChange: (field: keyof ClientFormData, checked: boolean) => void;
  onFieldBlur?: (field: keyof ClientFormData) => void;
  isLoading?: boolean;
  errors?: ValidationError[];
  touchedFields?: Set<string>;
  isCustomized?: boolean;
  granularCustomizedFields?: Record<string, boolean>;
  onRevert?: () => void;
  noCard?: boolean;
  hideTitle?: boolean;
  hideRevert?: boolean;
  hideToggle?: boolean;
}

export const ClientHolidayConfig: React.FC<ClientHolidayConfigProps> = ({
  formData,
  handleChange,
  handleSwitchChange,
  onFieldBlur,
  isLoading,
  errors = [],
  touchedFields = new Set(),
  isCustomized,
  granularCustomizedFields,
  onRevert,
  noCard,
  hideTitle,
  hideRevert,
  hideToggle,
}) => {
  // Helper function to get validation error for a field (only if touched)
  const getFieldError = (fieldName: string, align: 'left' | 'right' | 'center' = 'right') => {
    if (!touchedFields.has(fieldName)) return null;
    const error = errors.find(error => error.field === fieldName);
    const alignClass = {
        'left': 'text-left',
        'right': 'text-right',
        'center': 'text-center'
    }[align];
    return error ? <p className={cn("text-xs text-destructive mt-1", alignClass)}>{error.message}</p> : null;
  };

  const userState = useUserConfigState();

  // Three-tier inheritance for holiday selections: if the client hasn't
  // overridden catalog/custom holiday ids, show the user's defaults as the
  // effective value. The overridden_fields JSONB is exposed by the view as
  // holiday_overridden_fields; it tracks which fields the client has
  // explicitly customized vs. inherited from user defaults.
  const holidayOverrides = (formData as Record<string, unknown>).holiday_overridden_fields as Record<string, boolean> | undefined;
  const formCatalogIds = (formData as Record<string, unknown>).catalog_holiday_ids as string[] | undefined;
  const formCustomIds = (formData as Record<string, unknown>).custom_holiday_ids as string[] | undefined;
  const effectiveCatalogIds = useMemo(() => {
    if (holidayOverrides?.catalog_holiday_ids) {
      return formCatalogIds ?? [];
    }
    return userState.holidayCatalogIds;
  }, [holidayOverrides, formCatalogIds, userState.holidayCatalogIds]);
  const effectiveCustomIds = useMemo(() => {
    if (holidayOverrides?.custom_holiday_ids) {
      return formCustomIds ?? [];
    }
    return userState.holidayCustomIds;
  }, [holidayOverrides, formCustomIds, userState.holidayCustomIds]);

  const onSelectedCatalogIdsChange = useCallback((ids: string[]) => {
    handleChange('catalog_holiday_ids' as keyof ClientFormData, ids as ClientFormData[keyof ClientFormData]);
  }, [handleChange]);

  const onSelectedCustomIdsChange = useCallback((ids: string[]) => {
    handleChange('custom_holiday_ids' as keyof ClientFormData, ids as ClientFormData[keyof ClientFormData]);
  }, [handleChange]);

  const handleSurchargeTypeChange = useCallback((v: string) => {
    handleChange('holiday_premium_type', v);
    if (v === 'hourly_premium' && !formData.holiday_hourly_premium_type) {
      handleChange('holiday_hourly_premium_type', 'fixed');
    }
  }, [handleChange, formData.holiday_hourly_premium_type]);

  const onApplyHolidayPremiumChange = useCallback(
    (checked: boolean) => handleSwitchChange('apply_holiday_premium', checked),
    [handleSwitchChange],
  );
  const onHolidayOffsetAmountChange = useCallback(
    (amount: number) => handleChange('holiday_overtime_offset', amount),
    [handleChange],
  );
  const onMultiplierFloorCodeChange = useCallback(
    (v: string | null) => handleChange('holiday_floor_tier_code', v),
    [handleChange],
  );
  const onMultiplierFloorCustomValueChange = useCallback(
    (v: number | null) => handleChange('holiday_floor_tier_custom_value', v),
    [handleChange],
  );
  const onHolidayHourlyPremiumTypeChange = useCallback(
    (v: 'percentage' | 'fixed') => handleChange('holiday_hourly_premium_type', v),
    [handleChange],
  );

  return (
    <HolidayConfigForm
      title={hideTitle ? "" : "Holiday Premium"}
      description={hideTitle ? "" : "Override default holiday settings for this client."}
      isCustomized={isCustomized}
      onRevert={hideRevert ? undefined : onRevert}
      applyHolidayPremium={formData.apply_holiday_premium ?? false}
      onApplyHolidayPremiumChange={onApplyHolidayPremiumChange}
      holidayPremiumType={(formData.holiday_premium_type as 'offset_overtime' | 'fixed_dollar' | 'hourly_premium' | 'guaranteed_rate') || 'offset_overtime'}
      onHolidayPremiumTypeChange={handleSurchargeTypeChange}
      holidayOffsetAmount={formData.holiday_overtime_offset ?? 1}
      onHolidayOffsetAmountChange={onHolidayOffsetAmountChange}
      holidayMultiplierFloorCode={formData.holiday_floor_tier_code ?? null}
      onHolidayMultiplierFloorCodeChange={onMultiplierFloorCodeChange}
      holidayMultiplierFloorCustomValue={formData.holiday_floor_tier_custom_value ?? null}
      onHolidayMultiplierFloorCustomValueChange={onMultiplierFloorCustomValueChange}
      holidayFixedPremiumAmount={formData.holiday_fixed_premium_amount ?? null}
      rateMultipliers={formData.rate_multipliers || []}
      roles={formData.roles}
      holidaySelection={{
        selectedCatalogIds: effectiveCatalogIds,
        selectedCustomIds: effectiveCustomIds,
        onSelectedCatalogIdsChange,
        onSelectedCustomIdsChange,
      }}
      fixedSurchargeAmountProps={{
        value: formData.holiday_fixed_premium_amount ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('holiday_fixed_premium_amount', e.target.value ? parseFloat(e.target.value) : null),
        id: 'holiday_fixed_premium_amount',
        onBlur: () => onFieldBlur?.('holiday_fixed_premium_amount'),
        disabled: isLoading,
        'data-validation-key': 'holiday_fixed_premium_amount',
      }}
      fixedSurchargeAmountError={getFieldError('holiday_fixed_premium_amount')}
      holidayMultiplierError={getFieldError('holiday_floor_tier_code')}
      holidaySelectionError={getFieldError('holidays', 'center')}
      holidayHourlyPremiumType={formData.holiday_hourly_premium_type as 'percentage' | 'fixed' | null}
      onHolidayHourlyPremiumTypeChange={onHolidayHourlyPremiumTypeChange}
      holidayUpliftPercentage={formData.holiday_hourly_premium_type === 'percentage' ? (formData.holiday_hourly_premium_value ?? null) : null}
      holidayUpliftAmount={formData.holiday_hourly_premium_type === 'fixed' ? (formData.holiday_hourly_premium_value ?? null) : null}
      holidayUpliftPercentageProps={{
        value: formData.holiday_hourly_premium_type === 'percentage' ? (formData.holiday_hourly_premium_value ?? '') : '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('holiday_hourly_premium_value', e.target.value ? parseFloat(e.target.value) : null),
        id: 'holiday_hourly_premium_value',
        onBlur: () => onFieldBlur?.('holiday_hourly_premium_value'),
        disabled: isLoading,
        'data-validation-key': 'holiday_hourly_premium_value',
      }}
      holidayUpliftPercentageError={getFieldError('holiday_hourly_premium_value')}
      holidayUpliftAmountProps={{
        value: formData.holiday_hourly_premium_type === 'fixed' ? (formData.holiday_hourly_premium_value ?? '') : '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('holiday_hourly_premium_value', e.target.value ? parseFloat(e.target.value) : null),
        id: 'holiday_hourly_premium_value',
        onBlur: () => onFieldBlur?.('holiday_hourly_premium_value'),
        disabled: isLoading,
        'data-validation-key': 'holiday_hourly_premium_value',
      }}
      holidayUpliftAmountError={getFieldError('holiday_hourly_premium_value')}
      holidayApplyPremiumAfterOvertime={formData.holiday_premium_basis === 'ot_inclusive'}
      onHolidayApplyPremiumAfterOvertimeChange={(checked) => handleChange('holiday_premium_basis', checked ? 'ot_inclusive' : 'base_rate')}
      holidayApplyOnHalfDays={formData.holiday_apply_on_half_days ?? true}
      onHolidayApplyOnHalfDaysChange={(checked) => handleSwitchChange('holiday_apply_on_half_days', checked)}
      noCard={noCard}
      customizedFields={granularCustomizedFields}
      context="client"
      hideToggle={hideToggle}
      extendedOvertimeEnabled={(formData as Record<string, unknown>).holiday_extended_overtime_enabled as boolean ?? false}
      onExtendedOvertimeEnabledChange={(v) => handleChange('holiday_extended_overtime_enabled' as keyof ClientFormData, v)}
      extendedOvertimeTiers={(formData as Record<string, unknown>).holiday_extended_overtime_tiers as ExtendedTier[] ?? []}
      onExtendedOvertimeTiersChange={(tiers) => handleChange('holiday_extended_overtime_tiers' as keyof ClientFormData, tiers as unknown as ClientFormData[keyof ClientFormData])}
    />
  );
};
